/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useUpdateProfileMutation } from 'api/account'
import { Address } from 'models/account'
import { useAppSelector } from 'store/hooks'
import {
    flattenObject,
    getStatesByCountryCode,
    regex,
    removePropertyByKeys,
    replaceNullToEmptyString
} from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<Partial<Address>>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EditDetailsForm({ form }: Props) {
    const { appSession: { user, account } } = useAppSelector((state) => state.app)
    const [updateProfile] = useUpdateProfileMutation({ fixedCacheKey: 'update-profile' })
    const profile = replaceNullToEmptyString(removePropertyByKeys(flattenObject(user!), [
        'id',
        'date_of_birth',
        'accountNumber',
        'role',
        'account_holder',
        'account_number',
        'host',
        'ip',
        'security_answer',
        'time',
        'type',
        'customer_since',
        'updated_at',
        'question'
    ]))

    function UpdateState() {
        const { values, initialValues, setFieldValue } = useFormikContext<{ country: string, state: string }>()

        useEffect(() => {
            if (values.country !== initialValues.country && values.state === initialValues.state) {
                setFieldValue('state', getStatesByCountryCode(values.country)[0].state_code)
            }
        }, [values, initialValues, setFieldValue, form.current])

        return null
    }

    function getIntlCode(country: string = '') {
        return country === 'AU' ? ['+61'] : ['+64']
    }

    return (
        <Formik
            innerRef={form}
            initialValues={{
                ...profile,
                country: 'AU'
            }}
            validationSchema={Yup.object({
                firstname: Yup.string()
                    .required('Required'),
                lastname: Yup.string()
                    .required('Required'),
                phone: Yup.string()
                    .required('Required')
                    .matches(regex.phone, 'Invalid phone number'),
                address1: Yup.string()
                    .required('Required'),
                postcode: Yup.number()
                    .typeError('Invalid postcode')
                    .required('Required')
                    .min(1000, 'Invalid postcode')
                    .max(9999, 'Invalid postcode')
            })}
            onSubmit={({ address1, address2, city, country, postcode, state, ...rest }) => {
                updateProfile({
                    address: { address1, address2, country, state, city, postcode },
                    ...rest
                })
            }}
        >
            {({ isSubmitting, values }) => (
                <Form id={'editDetailsForm'}>
                    <Row>
                        <Field
                            id={'firstname'}
                            label={'First Name'}
                            name={'firstname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'lastname'}
                            label={'Last Name'}
                            name={'lastname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Field
                        id={'phone'}
                        label={'Phone Number'}
                        name={'phone'}
                        type={'text'}
                        prefixes={getIntlCode(values.country)}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'email'}
                        label={'Email Address'}
                        name={'email'}
                        type={'text'}
                        disabled={true}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'company'}
                        label={'Company Name'}
                        name={'company'}
                        type={'text'}
                        disabled={account?.role?.name !== 'account holder' || isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'address1'}
                        label={'Address 1'}
                        name={'address1'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'address2'}
                        label={'Address 2'}
                        name={'address2'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Row>
                        <Field
                            id={'suburb'}
                            label={'Suburb'}
                            name={'city'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'postcode'}
                            label={'Postcode'}
                            name={'postcode'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Row>
                        <Field
                            id={'state'}
                            label={'State'}
                            name={'state'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={getStatesByCountryCode(values.country).map(({ state_code: stateCode, state }) => (
                                { label: state, value: stateCode }
                            ))}
                            component={NXUIForm.SelectField}
                        />
                        <Field
                            id={'country'}
                            label={'Country'}
                            name={'country'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={[
                                { value: 'AU', label: 'Australia' },
                                { value: 'NZ', label: 'New Zealand' }
                            ]}
                            component={NXUIForm.SelectField}
                        />
                    </Row>
                    <UpdateState/>
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Row = styled.div`
    display: flex;
    justify-content: space-between;

    > div {
        width: 48%;
    }
`
